<template>
    <!-- Новий дизайн (?v2): усе меню міста одним гортанням — секції категорій одна за одною.
         Маршрути /lutsk і /lutsk/category/:code показують цей самий компонент (router.js), тож адреса
         міняється router.replace без перемонтування; ліва панель категорій — навігація по секціях -->
    <div class="bp-menu">
        <div class="bp-menu-bar">
            <!-- Пошук, сортування, «Інгредієнти», «Склади свою піцу», «Випадкова піца» і «На компанію» — один ряд;
                 коли вузько, кнопки лише з іконкою (container query у v2.css), на телефоні кнопки — рядом під пошуком.
                 Які кнопки показуємо — src/v2features.js; коли лишається саме сортування (solo), кружечки видів стають
                 у той самий ряд одразу після нього -->
            <div class="bp-menu-find" :class="{ 'bp-menu-find--solo': solo }">
            <div class="bp-search">
                <span class="bp-search__icon" v-html="icons.search"></span>
                <input class="bp-search__input" type="text" maxlength="30" v-model.trim="query" ref="search"
                       placeholder="Знайди піцу чи напій" aria-label="Пошук у меню" autocomplete="off"
                       @keydown.esc="clearSearch()">
                <button type="button" class="bp-search__clear" aria-label="Очистити пошук" v-if="query"
                        @click="clearSearch(true)" v-html="icons.close"></button>
            </div>
            <BpSortMenu v-if="loaded" v-model="sort"/>
            <PizzaFilters v-if="loaded && solo" :products="foundPizzas" v-model="pizzaKind"/>
            <button v-if="features.ingredients && loaded && allPizzas.length > 1" type="button" class="bp-rnd-btn bp-ing-btn" :class="{ 'is-on': ingredients.length }"
                    :aria-label="'Фільтр інгредієнтів' + (ingredients.length ? ': вибрано ' + ingredients.length : '')" title="Фільтр інгредієнтів"
                    @click="ingOpen = true"><span class="bp-rnd-btn__ic" v-html="icons.sliders"></span><span class="bp-rnd-btn__t">Інгредієнти</span><span class="bp-ing-btn__n" v-if="ingredients.length">{{ ingredients.length }}</span></button>
            <button v-if="features.ownPizza && ownPizza" type="button" class="bp-rnd-btn bp-own-btn" :aria-label="ownDone ? 'Склади свою піцу: додано в кошик' : 'Склади свою піцу'"
                    title="Склади свою піцу" @click="ownOpen = true"><span class="bp-rnd-btn__ic" v-html="ownDone ? icons.check : icons.chefHat"></span><span class="bp-rnd-btn__t">{{ ownDone ? 'Додано' : 'Склади свою піцу' }}</span></button>
            <button v-if="features.randomPizza && loaded && allPizzas.length > 1" type="button" class="bp-rnd-btn" aria-label="Випадкова піца"
                    title="Випадкова піца" @click="randomOpen = true"><span class="bp-rnd-btn__ic" v-html="icons.dice"></span><span class="bp-rnd-btn__t">Випадкова піца</span></button>
            <button v-if="features.crowd && loaded && allPizzas.length > 1" type="button" class="bp-rnd-btn" aria-label="Скільки піци на компанію"
                    title="Скільки піци на компанію" @click="crowdOpen = true"><span class="bp-rnd-btn__ic" v-html="icons.users"></span><span class="bp-rnd-btn__t">На компанію</span></button>
            </div>
            <PizzaFilters v-if="loaded && !solo" :products="foundPizzas" v-model="pizzaKind"/>
        </div>

        <!-- Вибрані інгредієнти — під смугою пошуку, кожен знімається окремо -->
        <div v-if="loaded && ingredients.length" class="bp-ing-active" role="status">
            <span class="bp-ing-active__found">{{ foundPizzas.length }} {{ pizzasWord(foundPizzas.length) }} з інгредієнтами:</span>
            <button v-for="term in ingredients" :key="term" type="button" class="bp-ing-active__chip"
                    :aria-label="'Прибрати ' + term" @click="removeIngredient(term)">{{ termLabel(term) }}<span v-html="icons.close"></span></button>
            <button type="button" class="bp-ing-active__reset" @click="ingredients = []">Скинути</button>
        </div>

        <BpIngredientFilter v-if="ingOpen" :pizzas="searchPizzas" :value="ingredients" @close="ingOpen = false" @apply="applyIngredients"/>
        <BpRandomPizza v-if="randomOpen" :pizzas="randomPool" @close="randomOpen = false" @order="orderRandom"/>
        <BpCrowdPizza v-if="crowdOpen" :pizzas="allPizzas" @close="crowdOpen = false"/>
        <BpCustomPizza v-if="ownOpen" :pizza="ownPizza" @close="ownOpen = false" @added="ownAdded()"/>

        <div v-if="!loaded" class="bp-menu-loading">
            <img src="@/assets/loading.svg" alt="Завантаження" style="width: 34px;">
        </div>

        <template v-else>
            <section v-for="section in visibleSections" :key="section.code" class="bp-menu-sec"
                     :id="'menu-' + section.code" :data-code="section.code" :aria-labelledby="'menu-h-' + section.code">
                <h2 class="bp-menu-sec__title" :id="'menu-h-' + section.code">{{ section.name }}</h2>
                <div class="products-pizza-container">
                    <div v-for="product in section.items" :key="product.id">
                        <Product :product="product"/>
                    </div>
                </div>
            </section>
            <div v-if="!visibleSections.length" class="bp-menu-empty" role="status">
                Нічого не знайшли<span v-if="query"> за «{{ query }}»</span>.
                <button type="button" @click="resetAll()">Показати все меню</button>
            </div>
        </template>
    </div>
</template>

<script>
import Product from '@/components/Product.vue'
import PizzaFilters from '@/components/PizzaFilters.vue'
import BpRandomPizza from '@/components/BpRandomPizza.vue'
import BpIngredientFilter from '@/components/BpIngredientFilter.vue'
import BpCustomPizza from '@/components/BpCustomPizza.vue'
import BpCrowdPizza from '@/components/BpCrowdPizza.vue'
import BpSortMenu from '@/components/BpSortMenu.vue'
import { sortProducts } from '@/menuSort'
import { loadCustomPizza } from '@/customPizza'
import { hasAll, pizzasWord, termLabel } from '@/ingredientGroups'
import { pizzaIs } from '@/pizzaKinds'
import { ICONS } from '@/v2icons'
import { V2_FEATURES } from '@/v2features'

const PIZZA = 'pizza';
const isMenuRoute = route => route.name == 'pizza' || route.name == 'category.index';
const norm = text => String(text || '').toLowerCase().replace(/ʼ|’/g, "'");

export default {
    name: 'Menu',
    components: {
        Product,
        PizzaFilters,
        BpRandomPizza,
        BpIngredientFilter,
        BpCustomPizza,
        BpCrowdPizza,
        BpSortMenu
    },
    data() {
        return {
            icons: ICONS,
            features: V2_FEATURES,
            categories: [],
            products: [],
            loaded: false,
            query: '',
            pizzaKind: null,
            // Код секції, що зараз під шапкою (її ж підсвічує ліва панель через адресу)
            active: PIZZA,
            lockTimer: null,
            locked: false,
            randomOpen: false,
            crowdOpen: false,
            // Сортування: popular (як в адмінці), cheap, expensive — src/menuSort.js
            sort: 'popular',
            // «Склади свою піцу»: товар-основа (src/customPizza.js), відкрите вікно, «Додано» на кнопці 1,6 с
            ownPizza: null,
            ownOpen: false,
            ownDone: false,
            ownTimer: null,
            // «Фільтр інгредієнтів»: вибрані інгредієнти (рядки складу в нижньому регістрі)
            ingredients: [],
            ingOpen: false
        }
    },

    computed: {
        // У ряду пошуку лишилось саме сортування — решта кнопок схована (src/v2features.js)
        solo() {
            return !this.features.ingredients && !this.features.ownPizza && !this.features.randomPizza && !this.features.crowd;
        },
        // Секції: категорії верхнього рівня, у кожній — товари її самої та вкладених категорій (як у views/Product.vue)
        sections() {
            return this.categories
                .filter(category => category.is_enabled == 1 && !category.parent_id)
                .map(category => {
                    const ids = this.descendants(category.id);
                    return {
                        code: category.code,
                        name: category.name,
                        items: this.products.filter(product => ids.includes(product.category_id))
                    };
                })
                .filter(section => section.items.length);
        },

        // Пошук: назва або склад, без урахування регістру — по всіх секціях
        searched() {
            const q = norm(this.query);
            return this.sections.map(section => Object.assign({}, section, {
                items: q ? section.items.filter(product => norm(product.title).includes(q) || norm(product.description).includes(q)) : section.items
            }));
        },

        // Піци після пошуку — з них вибирає «Фільтр інгредієнтів»
        searchPizzas() {
            const pizza = this.searched.find(section => section.code == PIZZA);
            return pizza ? pizza.items : [];
        },

        // Пошук і «Фільтр інгредієнтів» (лише піци: у них є все вибране)
        found() {
            if (!this.ingredients.length) return this.searched;
            return this.searched.map(section => section.code == PIZZA
                ? Object.assign({}, section, { items: section.items.filter(product => hasAll(product, this.ingredients)) })
                : section);
        },

        // Лічильники чипів — по піцах, що лишились після пошуку
        foundPizzas() {
            const pizza = this.found.find(section => section.code == PIZZA);
            return pizza ? pizza.items : [];
        },

        allPizzas() {
            const pizza = this.sections.find(section => section.code == PIZZA);
            return pizza ? pizza.items : [];
        },

        // Випадкова піца — серед показаних (з урахуванням пошуку й чипа «Гострі…»), а коли показано менше двох — з усіх
        randomPool() {
            const shown = this.visibleSections.find(section => section.code == PIZZA);
            return shown && shown.items.length > 1 ? shown.items : this.allPizzas;
        },

        // Чипи «Гострі / Ситні / …» фільтрують лише піци; сортування — у кожній секції окремо
        visibleSections() {
            return this.found
                .map(section => section.code == PIZZA && this.pizzaKind
                    ? Object.assign({}, section, { items: section.items.filter(product => pizzaIs(this.pizzaKind, product)) })
                    : section)
                .filter(section => section.items.length)
                .map(section => this.sort == 'popular' ? section : Object.assign({}, section, { items: sortProducts(section.items, this.sort) }));
        }
    },

    watch: {
        // Адресу змінили ззовні (назад/вперед, «Головна» з меню) — гортаємо до секції
        $route(to) {
            if (!isMenuRoute(to) || !this.loaded) return;
            const code = this.sectionFor(to);
            if (code != this.active) {
                this.go(code, false);
            }
        },

        visibleSections() {
            this.$nextTick(() => this.observe());
        }
    },

    mounted() {
        document.querySelector('body').style.overflow = '';
        this.$root.$on('bp-menu-go', this.go);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        if (V2_FEATURES.ownPizza) loadCustomPizza().then(pizza => { this.ownPizza = pizza; });

        Promise.all([this.$store.dispatch('getCategories'), this.$store.dispatch('getProducts')]).then(([categories, products]) => {
            this.categories = categories;
            this.products = products;
            this.loaded = true;
            this.$nextTick(() => {
                this.observe();
                const code = this.sectionFor(this.$route);
                this.active = code;
                if (this.$route.name == 'category.index') {
                    this.jumpTo(code);
                }
            });
        });
    },

    beforeDestroy() {
        this.$root.$off('bp-menu-go', this.go);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.ownTimer);
        clearTimeout(this.lockTimer);
        if (this.observer) this.observer.disconnect();
        this.stopRefit();
    },

    methods: {
        descendants(id) {
            const ids = [id];
            for (let i = 0; i < ids.length; i++) {
                this.categories.forEach(category => {
                    if (category.parent_id == ids[i] && !ids.includes(category.id)) ids.push(category.id);
                });
            }
            return ids;
        },

        // Код секції для маршруту: /lutsk — піца, /lutsk/category/snacks — секція «Інше» (батько снеків)
        sectionFor(route) {
            if (route.name != 'category.index') return PIZZA;
            let category = this.categories.find(c => c.code == route.params.code);
            while (category && category.parent_id) {
                const parentId = category.parent_id;
                category = this.categories.find(c => c.id == parentId);
            }
            const code = category ? category.code : PIZZA;
            return this.sections.some(section => section.code == code) ? code : PIZZA;
        },

        routeFor(code) {
            const params = { locationCity: this.$route.params.locationCity };
            return code == PIZZA ? { name: 'pizza', params } : { name: 'category.index', params: Object.assign({ code }, params) };
        },

        // Низ липкої шапки + проміжок: під нею зупиняється заголовок секції
        offset() {
            const header = document.querySelector('header');
            return (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 12;
        },

        target(code) {
            // Піца — перша секція: показуємо разом із пошуком і чипами над нею
            if (code == PIZZA) return this.$el;
            return this.$el.querySelector('#menu-' + code);
        },

        scrollTop(code) {
            const el = this.target(code);
            return el ? Math.max(0, Math.round(el.getBoundingClientRect().top + window.pageYOffset - this.offset())) : null;
        },

        // Клік у лівій панелі (подія з LocationCityBase) або зміна адреси
        go(code, replaceRoute = true) {
            if (!this.sections.some(section => section.code == code)) return;
            // Секцію сховав пошук чи фільтр — скидаємо їх, інакше гортати нікуди
            if (!this.visibleSections.some(section => section.code == code)) {
                this.query = '';
                this.pizzaKind = null;
                this.ingredients = [];
            }
            this.active = code;
            if (replaceRoute) this.replaceRoute(code);
            this.$nextTick(() => {
                const top = this.scrollTop(code);
                if (top === null) return;
                this.lock();
                window.scrollTo({ top, behavior: 'smooth' });
            });
        },

        // Відкрили адресу секції (/lutsk/category/beverages) — одразу там; поки вантажаться фото вище, тримаємо позицію
        jumpTo(code) {
            const top = this.scrollTop(code);
            if (top === null) return;
            this.lock();
            window.scrollTo(0, top);
            this.stopRefit();
            const refit = () => {
                const next = this.scrollTop(code);
                if (next !== null) window.scrollTo(0, next);
            };
            const stop = () => this.stopRefit();
            this.$el.addEventListener('load', refit, true);
            ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(name => window.addEventListener(name, stop, { passive: true }));
            const timer = setTimeout(stop, 4000);
            this.refitOff = () => {
                clearTimeout(timer);
                this.$el.removeEventListener('load', refit, true);
                ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(name => window.removeEventListener(name, stop));
            };
        },

        stopRefit() {
            if (this.refitOff) {
                this.refitOff();
                this.refitOff = null;
            }
        },

        replaceRoute(code) {
            const to = this.routeFor(code);
            if (to.name == this.$route.name && to.params.code == this.$route.params.code) return;
            this.$router.replace(to, () => {}, () => {});
        },

        // Поки йде програмне гортання, підсвітку не перемикаємо (вона вже на цілі)
        lock() {
            this.locked = true;
            clearTimeout(this.lockTimer);
            this.lockTimer = setTimeout(() => { this.locked = false; }, 900);
        },

        setActive(code) {
            // Відкрите вікно «В кошик» гортає сторінку нагору — це не перехід по меню
            if (this.locked || code == this.active || document.querySelector('body').style.overflow == 'hidden') return;
            this.active = code;
            this.replaceRoute(code);
        },

        // Лінія на висоті низу шапки: секція, що її перетинає, — поточна
        observe() {
            if (this.observer) this.observer.disconnect();
            if (!('IntersectionObserver' in window) || !this.$el.querySelectorAll) return;
            const top = this.offset() + 8;
            const bottom = Math.max(0, window.innerHeight - top - 1);
            this.observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) this.setActive(entry.target.getAttribute('data-code'));
                });
            }, { rootMargin: `-${top}px 0px -${bottom}px 0px` });
            this.$el.querySelectorAll('.bp-menu-sec').forEach(el => this.observer.observe(el));
        },

        ownAdded() {
            clearTimeout(this.ownTimer);
            this.ownDone = true;
            this.ownTimer = setTimeout(() => { this.ownDone = false; }, 1600);
        },

        onResize() {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => this.observe(), 150);
        },

        onScroll() {
            if (this.locked) {
                // Гортання ще триває — подовжуємо блокування до його кінця
                this.lock();
                return;
            }
            // Унизу сторінки коротка остання секція може не дійти до лінії — тоді поточна вона
            const doc = document.documentElement;
            if (window.innerHeight + window.pageYOffset >= doc.scrollHeight - 4) {
                const last = this.visibleSections[this.visibleSections.length - 1];
                if (last) this.setActive(last.code);
            }
        },

        // «Замовити» у випадковій піці відкриває вікно її картки в меню; прихована пошуком чи чипом — спершу показуємо все
        orderRandom(pizza) {
            this.randomOpen = false;
            const card = () => this.$children.find(child => child.$options.name == 'Product' && child.product && child.product.id == pizza.id);
            const open = () => { const c = card(); if (c) c.showAddToCardModal(); };
            if (card()) return this.$nextTick(open);
            this.query = '';
            this.pizzaKind = null;
            this.ingredients = [];
            this.$nextTick(open);
        },

        // «Показати N піц» у фільтрі: застосовуємо й гортаємо до піц
        applyIngredients(selected) {
            this.ingOpen = false;
            this.ingredients = selected.slice();
            this.pizzaKind = null;
            this.$nextTick(() => this.go(PIZZA));
        },

        removeIngredient(term) {
            this.ingredients = this.ingredients.filter(item => item !== term);
        },

        pizzasWord,
        termLabel,

        resetAll() {
            this.pizzaKind = null;
            this.ingredients = [];
            this.clearSearch(true);
        },

        clearSearch(focus) {
            this.query = '';
            if (focus && this.$refs.search) this.$refs.search.focus();
        }
    }
}
</script>
